/**
 * PasskeyManager — iter195 Phase 3.
 *
 * Lists existing passkeys + lets the user enroll a new one. Mounted inside
 * the Profile/Settings card. Hides itself entirely when the device does
 * not support platform passkeys (no Face ID / Touch ID / Windows Hello).
 */
import { useCallback, useEffect, useState } from 'react';
import { Fingerprint, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  passkeysSupported,
  platformAuthenticatorAvailable,
  registerPasskey,
  listPasskeys,
  deletePasskey,
} from '../lib/passkey';
import { trackPasskeyEnrolled, trackPasskeyDeleted } from '../lib/analytics';

function inferDeviceName() {
  try {
    const ua = navigator.userAgent || '';
    if (/iPhone/i.test(ua)) return 'iPhone';
    if (/iPad/i.test(ua)) return 'iPad';
    if (/Macintosh/i.test(ua)) return 'Mac';
    if (/Windows/i.test(ua)) return 'Windows PC';
    if (/Android/i.test(ua)) return 'Android device';
    if (/Linux/i.test(ua)) return 'Linux';
  } catch { /* ignore */ }
  return 'This device';
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

export default function PasskeyManager({ userPhone }) {
  const [supported, setSupported] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const docs = await listPasskeys();
      setItems(Array.isArray(docs) ? docs : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const isSupported = passkeysSupported() && (await platformAuthenticatorAvailable());
      setSupported(isSupported);
      if (isSupported) reload();
      else setLoading(false);
    })();
  }, [reload]);

  if (!supported) return null;

  const handleEnroll = async () => {
    if (enrolling) return;
    setEnrolling(true);
    try {
      await registerPasskey(inferDeviceName());
      trackPasskeyEnrolled('profile');
      toast.success('Face ID enabled — sign in with one tap next time');
      if (userPhone) {
        try { localStorage.setItem('pref_last_passkey_phone', userPhone); } catch { /* ignore */ }
      }
      await reload();
    } catch (e) {
      // iter204 — Surface the exact failure so users can self-diagnose
      // (especially on Android Credential Manager which throws generic
      // "An unknown error occurred while talking to the credential manager"
      // for several different underlying causes).
      const name = e?.name || '';
      const detail = e?.response?.data?.detail || e?.message || 'Could not enroll passkey';
      // Map of well-known WebAuthn / Credential Manager errors → friendly hint.
      const HINTS = {
        NotAllowedError: 'Enrollment cancelled.',
        InvalidStateError: 'This device already has a passkey for your account.',
        NotSupportedError: 'Your browser does not support passkeys. Try Chrome.',
        SecurityError: 'Secure context required. Open this site in your browser (not embedded webview).',
        ConstraintError: 'Your device cannot create the requested passkey type.',
        AbortError: 'Enrollment timed out — please try again.',
      };
      const isEdgeAndroid = /Android/i.test(navigator.userAgent) && /Edg(A|e)/i.test(navigator.userAgent);
      const isCredManagerError = /credential manager/i.test(detail);
      if (name === 'NotAllowedError') {
        toast.info(HINTS.NotAllowedError);
      } else if (isCredManagerError && isEdgeAndroid) {
        // The common iter204 case — Edge on Android PWA hitting Credential
        // Manager. Steer the user to Chrome where the flow works reliably.
        toast.error(
          'Passkey enrollment failed in Edge. Please open https://split-easy.in in Chrome on this device and try again.',
          { duration: 9000 }
        );
      } else if (HINTS[name]) {
        toast.error(`${HINTS[name]} (${name})`);
      } else {
        toast.error(`${detail}${name ? ` (${name})` : ''}`);
      }
      // Always log to console for power users / support.
      console.error('[passkey enroll]', { name, message: e?.message, detail, stack: e?.stack });
    } finally {
      setEnrolling(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deletePasskey(id);
      trackPasskeyDeleted();
      toast.success('Passkey removed');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove passkey');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-4 border-b border-border" data-testid="passkey-manager">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <Fingerprint className="w-5 h-5 text-primary" />
          <div>
            <p className="font-semibold text-sm">Sign in with Face ID</p>
            <p className="text-xs text-muted-foreground">
              Skip the password — unlock with your fingerprint or face
            </p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={handleEnroll}
          disabled={enrolling}
          data-testid="passkey-enroll-btn"
          className="rounded-full h-9 px-3 text-xs font-bold"
        >
          {enrolling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
          {enrolling ? 'Enrolling…' : 'Add'}
        </Button>
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground inline-flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="passkey-empty-state">
          No passkeys yet. Tap <strong>Add</strong> to enable biometric sign-in on this device.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="passkey-list">
          {items.map((pk) => (
            <li
              key={pk.id}
              className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card/50 px-3 py-2"
              data-testid={`passkey-row-${pk.id}`}
            >
              <div className="min-w-0">
                <p className="text-xs font-semibold truncate">{pk.device_name || 'Unnamed device'}</p>
                <p className="text-xs text-muted-foreground">
                  Added {formatDate(pk.created_at)}
                  {pk.last_used_at ? ` · Last used ${formatDate(pk.last_used_at)}` : ' · Never used'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleDelete(pk.id)}
                disabled={deletingId === pk.id}
                data-testid={`passkey-delete-${pk.id}`}
                aria-label="Remove passkey"
                className="shrink-0 p-1.5 rounded-full hover:bg-destructive/10 transition disabled:opacity-50"
              >
                {deletingId === pk.id
                  ? <Loader2 className="w-4 h-4 animate-spin text-destructive" />
                  : <Trash2 className="w-4 h-4 text-destructive" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
